import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const NavigationOverlay = ({ isVisible, onToggle }) => {
  const navigate = useNavigate();
  const [hoveredItem, setHoveredItem] = useState(null);

  const navigationItems = [
    {
      name: 'Portfolio Gallery',
      route: '/interactive-portfolio-gallery',
      icon: 'Briefcase',
      description: 'Explore my creative projects and case studies',
      color: '#00d4ff'
    },
    {
      name: 'Process Lab',
      route: '/process-laboratory',
      icon: 'Beaker',
      description: 'Discover my creative methodology and workflow',
      color: '#ff6b35'
    },
    {
      name: 'Skill Constellation',
      route: '/skill-constellation',
      icon: 'Star',
      description: 'Interactive visualization of my capabilities',
      color: '#00ff88'
    },
    {
      name: 'Contact Nexus',
      route: '/contact-nexus',
      icon: 'MessageCircle',
      description: 'Let\'s collaborate on your next project',
      color: '#ff3366'
    },
    {
      name: 'Experimental Playground',
      route: '/experimental-playground',
      icon: 'Zap',
      description: 'Beta features and creative experiments',
      color: '#ffaa00'
    }
  ];

  const handleNavigation = (route) => {
    navigate(route);
    onToggle();
  };

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 z-40 bg-background/95 backdrop-blur-md">
      <div className="container mx-auto px-6 py-8 h-full flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between mb-12">
          <div>
            <h2 className="font-headline font-bold text-3xl text-foreground mb-2">
              Navigation Hub
            </h2>
            <p className="font-body text-muted-foreground">
              Choose your destination in the creative universe
            </p>
          </div>
          
          <Button
            variant="ghost"
            onClick={onToggle}
            iconName="X"
            className="text-muted-foreground hover:text-foreground"
          >
            Close
          </Button>
        </div>

        {/* Navigation Grid */}
        <div className="flex-1 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
          {navigationItems?.map((item, index) => (
            <button
              key={item?.route}
              className="group relative glass-panel rounded-xl p-8 text-left transition-all duration-300 hover:scale-105 cosmic-glow-hover"
              onClick={() => handleNavigation(item?.route)}
              onMouseEnter={() => setHoveredItem(index)}
              onMouseLeave={() => setHoveredItem(null)}
              style={{
                animationDelay: `${index * 0.1}s`
              }}
            >
              {/* Icon */}
              <div className="mb-6">
                <div 
                  className="w-16 h-16 rounded-full flex items-center justify-center transition-all duration-300"
                  style={{
                    backgroundColor: `${item?.color}20`,
                    borderColor: `${item?.color}50`,
                    border: '2px solid'
                  }}
                >
                  <Icon 
                    name={item?.icon} 
                    size={28} 
                    style={{ color: item?.color }}
                  />
                </div>
              </div>

              {/* Content */}
              <div className="space-y-3">
                <h3 className="font-headline font-bold text-xl text-foreground group-hover:text-accent transition-colors">
                  {item?.name}
                </h3>
                <p className="font-body text-muted-foreground group-hover:text-foreground transition-colors">
                  {item?.description}
                </p>
              </div>

              {/* Arrow */}
              <div className="absolute top-6 right-6 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <Icon name="ArrowRight" size={20} className="text-accent" />
              </div>

              {/* Hover Effect */}
              {hoveredItem === index && (
                <div className="absolute inset-0 rounded-xl border-2 border-accent/50 pointer-events-none">
                  <div className="absolute inset-0 bg-accent/5 rounded-xl"></div>
                </div>
              )}
            </button>
          ))}
        </div>

        {/* Footer */}
        <div className="mt-12 text-center">
          <div className="flex items-center justify-center space-x-6 text-sm text-muted-foreground">
            <div className="flex items-center space-x-2">
              <Icon name="MousePointer" size={16} />
              <span>Click to navigate</span>
            </div>
            <div className="flex items-center space-x-2">
              <Icon name="Keyboard" size={16} />
              <span>ESC to close</span>
            </div>
          </div>
        </div>
      </div>
      {/* Floating Particles */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {[...Array(30)]?.map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 bg-accent/40 rounded-full animate-float"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 8}s`,
              animationDuration: `${6 + Math.random() * 6}s`
            }}
          />
        ))}
      </div>
    </div>
  );
};

export default NavigationOverlay;